import { PERCH_FUNNEL_ID } from '~/funnels/perch-energy/config';
import { IS_DEV, IS_STAGING } from '~/utils/constants';
import { store } from '~/utils/store';
import { trackError } from './tracking';

/**
 * Fire a DMI / Capital One Shopping client event (`window.c1s`) once the onboarding pixel has loaded.
 * Event names must be configured by your DMI program contact.
 */
export function fireDmiEvent(eventName: string, parameters?: Record<string, unknown>): void {
  const c1s = typeof window !== 'undefined' ? window.c1s : undefined;
  if (!c1s) {
    if (IS_DEV || IS_STAGING) {
      console.warn('[DMI] window.c1s is undefined — set DMI_PIXEL_SCRIPT_URL and rebuild.', {
        eventName,
      });
    }
    trackError(new Error('DMI Pixel Not Loaded'));
    return;
  }
  try {
    if (parameters !== undefined && Object.keys(parameters).length > 0) {
      c1s(eventName, parameters);
    } else {
      c1s(eventName);
    }
  } catch (error) {
    trackError(error instanceof Error ? error : new Error('DMI event failed'), {
      extras: { eventName, ...(parameters ? { parameters } : {}) },
    });
  }
}

/** Use for server-side `merchant-transactions` bodies when attribution was captured client-side. */
export function getDmiClickId(): string | undefined {
  const c1s = typeof window !== 'undefined' ? window.c1s : undefined;
  const getter = c1s?.getClickId;
  return typeof getter === 'function' ? getter.call(c1s) : undefined;
}

/* eslint-disable camelcase */
const PERCH_DMI_EVENT_MAPPING: Record<string, string> = {
  utility_step_started: 'perch_funnel_started',
  user_step_completed: 'account_created',
  enrollment_step_completed: 'utility_information_completed',
  contract_form_step_started: 'lmi_self_attestation_started',
  contract_form_step_completed: 'lmi_self_attestation_completed',
  ipa_document_group_started: 'il_df_signnow_started',
  ipa_document_group_completed: 'il_df_signnow_completed',
  upload_lmi_proof_docs_step_started: 'lmi_proof_docs_started',
  upload_lmi_proof_docs_step_completed: 'lmi_proof_docs_completed',
  legal_step_started: 'disclosure_form_subscriber_agreement_review_started',
  legal_step_completed: 'disclosure_form_subscriber_agreement_review_completed',
};
/* eslint-enable camelcase */

export function notifyDmiForSegmentEvent(
  segmentEventType: string,
  funnelId?: string,
  userId?: number | string,
): void {
  if (funnelId !== PERCH_FUNNEL_ID) {
    return;
  }

  const eventName = PERCH_DMI_EVENT_MAPPING[segmentEventType];
  if (!eventName) {
    return;
  }

  const resolvedUserId = userId ?? store.get('USER_ID');
  /* eslint-disable camelcase -- DMI parameters use snake_case */
  const parameters =
    resolvedUserId !== undefined ? { externalId: String(resolvedUserId) } : undefined;
  /* eslint-enable camelcase */
  fireDmiEvent(eventName, parameters);
}
